import React, { useState, useCallback, useRef } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import TreeView from './components/TreeView';
import ContoModal from './components/ContoModal';
import Giornale from './components/Giornale';
import LoginForm from './components/LoginForm';
import { useConti } from './hooks/useConti';
import { Plus, Settings, LogOut, Menu, FileText } from 'lucide-react';
import apiService from './services/api-mini';

// TPT: Set del componente Button E URL.

const REPORT_JOURNAL_URL = 'https://sql5090.site4now.net/Reports/report/mpolleschi-001/SMARTERASP/DEVIN/GLEDGER/transazioni'
const REPORT_PARTITARIO_URL = 'https://sql5090.site4now.net/Reports/report/mpolleschi-001/SMARTERASP/DEVIN/GLEDGER/partitario'
const REPORT_BILANCIO_URL = 'https://sql5090.site4now.net/Reports/report/mpolleschi-001/SMARTERASP/DEVIN/GLEDGER/conti'


function App() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [selectedConto, setSelectedConto] = useState(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [expandedNodes, setExpandedNodes] = useState(new Set());
  const [currentView, setCurrentView] = useState('conti'); // 'conti' o 'giornale'
  const mainContentRef = useRef(null);

  const { conti, loading, error, user, createConto, updateConto, deleteConto, moveConto, duplicateConto, login, logout } = useConti();

  console.log('useConti result:', { conti, loading, error, user, createConto, updateConto, deleteConto, moveConto, duplicateConto, login, logout });

  const handleNodeToggle = useCallback((nodeId) => {
    setExpandedNodes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(nodeId)) {
        newSet.delete(nodeId);
      } else {
        newSet.add(nodeId);
      }
      return newSet;
    });
  }, []);

  const handleViewChange = useCallback((view) => {
    setCurrentView(view);
    // Scroll all'inizio della pagina quando cambia vista
    window.scrollTo({ top: 0, behavior: 'smooth' });

    // Focus su un elemento specifico dopo un piccolo delay
    setTimeout(() => {
      if (mainContentRef.current) {
        mainContentRef.current.focus();
      }
    }, 300);
  }, []);

  const handleNodeAction = useCallback(async (action, node) => {
    switch (action) {
      case 'add':
        setSelectedConto({ parent: node.id });
        setModalMode('create');
        setIsModalOpen(true);
        break;
      case 'edit':
        setSelectedConto(node);
        setModalMode('edit');
        setIsModalOpen(true);
        break;
      case 'duplicate':
        try {
          await duplicateConto(node.id, `${node.name} (copia)`);
        } catch (err) {
          console.error('Errore duplicazione conto:', err);
        }
        break;
      case 'delete':
        try {
          await deleteConto(node.id);
        } catch (err) {
          console.error('Errore eliminazione conto:', err);
        }
        break;
    }
  }, [duplicateConto, deleteConto]);

  const handleNodeDrop = useCallback(async (draggedNodeId, targetNodeId) => {
    try {
      console.log('DROP:', { draggedNodeId, targetNodeId });
      await moveConto(draggedNodeId, targetNodeId);
    } catch (err) {
      console.error('Errore spostamento conto:', err);
    }
  }, [moveConto]);

  const handleAddRoot = useCallback(() => {
    setSelectedConto(null);
    setModalMode('create');
    setIsModalOpen(true);
  }, []);

  const handleModalClose = useCallback(() => {
    setIsModalOpen(false);
    setSelectedConto(null);
  }, []);

  const handleModalSubmit = useCallback(async (contoData) => {
    try {
      console.log('Modal submit:', { modalMode, selectedConto, contoData });

      if (modalMode === 'create') {
        await createConto(contoData);
      } else if (modalMode === 'edit' && selectedConto?.id) {
        await updateConto(selectedConto.id, contoData);
      } else {
        console.error('Modal mode non valido o selectedConto mancante');
        return;
      }
      handleModalClose();
    } catch (err) {
      console.error('Errore salvataggio conto:', err);
    }
  }, [modalMode, selectedConto, createConto, updateConto, handleModalClose]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Caricamento conti...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="bg-red-50 border border-red-200 text-red-700 px-6 py-4 rounded-lg">
            <p className="font-medium">Errore nel caricamento</p>
            <p className="text-sm mt-1">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  // Mostra LoginForm se non c'è utente
  if (!user) {
    return <LoginForm onLogin={login} />;
  }

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <header className="bg-white shadow-sm border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <div className="flex items-center">
                <button
                  onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                  className="md:hidden p-2 rounded-lg hover:bg-gray-100"
                >
                  <Menu className="w-5 h-5 text-gray-600" />
                </button>
                <h1 className="text-xl font-bold text-gray-900 ml-2 md:ml-0">GLEDGER</h1>
                {user && (
                  <span className="ml-4 text-sm text-gray-600">
                    Benvenuto, {user.username}
                  </span>
                )}
              </div>

              {/* Menu Navigazione */}

              <nav className="hidden md:flex items-center space-x-1">


                {/* TPT: buttons 4 FORMS*/}

                <button
                  onClick={() => handleViewChange('conti')}
                  className={`px-3 py-2 rounded-md text-sm font-medium transition-colors ${currentView === 'conti'
                      ? 'bg-blue-100 text-blue-700'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                    }`}
                >
                  Conti
                </button>



                <button
                  onClick={() => handleViewChange('giornale')}
                  className={`px-3 py-2 rounded-md text-sm font-medium transition-colors flex items-center gap-1 ${currentView === 'giornale'
                      ? 'bg-blue-100 text-blue-700'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                    }`}
                >
                  <FileText className="w-4 h-4" />
                  Giornale
                </button>

                {/* TPT: buttons reports*/}

                <button
                  variant="outline"
                  onClick={() => window.open(REPORT_JOURNAL_URL, '_blank', 'noopener')}
                  data-testid="open-journal-report-button"
                  title="Apri il report Journal (Reporting Services)"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Journal
                </button>


                <button
                  variant="outline"
                  onClick={() => window.open(REPORT_PARTITARIO_URL, '_blank', 'noopener')}
                  data-testid="open-partitario-report-button"
                  title="Apri il report Partitario (Reporting Services)"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Partitario
                </button>

                <button
                  variant="outline"
                  onClick={() => window.open(REPORT_BILANCIO_URL, '_blank', 'noopener')}
                  data-testid="open-bilancio-report-button"
                  title="Apri il report Bilancio (Reporting Services)"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  Bilancio
                </button>






              </nav>

              <div className="flex items-center space-x-2">
                {currentView === 'conti' && (
                  <button
                    onClick={handleAddRoot}
                    className="btn-primary flex items-center gap-2 text-sm"
                  >
                    <Plus className="w-4 h-4" />
                    <span className="hidden sm:inline">Nuovo Conto</span>
                  </button>
                )}
                <button className="p-2 rounded-lg hover:bg-gray-100">
                  <Settings className="w-5 h-5 text-gray-600" />
                </button>

                <button
                  onClick={logout}
                  className="p-2 rounded-lg hover:bg-gray-100"
                >
                  <LogOut className="w-5 h-5 text-gray-600" />
                </button>

              </div>
            </div>
          </div>


          {/* Mobile Menu */}
          {isMobileMenuOpen && (
            <div className="md:hidden border-t border-gray-200">
              <div className="px-2 pt-2 pb-3 space-y-1">
                <button
                  onClick={() => { handleViewChange('conti'); setIsMobileMenuOpen(false); }}
                  className={`block px-3 py-2 rounded-md text-base font-medium w-full text-left ${currentView === 'conti'
                      ? 'bg-blue-100 text-blue-700'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                    }`}
                >
                  Conti
                </button>
                <button
                  onClick={() => { handleViewChange('giornale'); setIsMobileMenuOpen(false); }}
                  className={`block px-3 py-2 rounded-md text-base font-medium w-full text-left ${currentView === 'giornale'
                      ? 'bg-blue-100 text-blue-700'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                    }`}
                >
                  Giornale
                </button>
                {currentView === 'conti' && (
                  <button
                    onClick={handleAddRoot}
                    className="block px-3 py-2 rounded-md text-base font-medium w-full text-left text-gray-600 hover:text-gray-900 hover:bg-gray-100"
                  >
                    Nuovo Conto
                  </button>
                )}
              </div>
            </div>
          )}
        </header>

        {/* Main Content */}
        <main
          ref={mainContentRef}
          className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 focus:outline-none transition-all duration-300 ${currentView === 'giornale' ? 'animate-fade-in' : 'animate-fade-in'
            }`}
          tabIndex={-1}
          role="main"
          aria-label={`Contenuto principale: ${currentView === 'conti' ? 'Gestione conti' : 'Giornale transazioni'}`}
        >
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* TreeView - Full width on mobile, 2/3 on desktop */}
            <div className="lg:col-span-2">
              {conti && conti.length > 0 ? (
                <TreeView
                  data={conti}
                  onNodeAction={handleNodeAction}
                  onNodeDrop={handleNodeDrop}
                  expandedNodes={expandedNodes}
                  onNodeToggle={handleNodeToggle}
                  selectedNode={selectedConto}
                />
              ) : (
                <div className="bg-white rounded-lg shadow p-6 text-center">
                  <h3 className="text-lg font-medium text-gray-900 mb-2">Nessun conto</h3>
                  <p className="text-gray-500 mb-4">Inizia creando il tuo primo conto</p>
                  <button onClick={handleAddRoot} className="btn-primary">
                    Crea Primo Conto
                  </button>
                </div>
              )}
            </div>

            {/* Sidebar - 1/3 on desktop */}
            <div className="space-y-6">
              {/* Stats */}
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Riepilogo</h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Totale Conti</span>
                    <span className="font-medium">{conti?.length || 0}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Attivi</span>
                    <span className="font-medium text-green-600">
                      {conti?.filter(c => c.stato === 'attivo').length || 0}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Passivi</span>
                    <span className="font-medium text-red-600">
                      {conti?.filter(c => c.stato === 'passivo').length || 0}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Transazionali</span>
                    <span className="font-medium text-blue-600">
                      {conti?.filter(c => c.hasTransactions).length || 0}
                    </span>
                  </div>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Guida Rapida</h3>
                <div className="space-y-2 text-sm text-gray-600">
                  <p>• <strong>Click destro:</strong> menu contestuale</p>
                  <p>• <strong>Drag & Drop:</strong> sposta conti</p>
                  <p>• <strong>Click:</strong> seleziona conto</p>
                  <p>• <strong>Doppio click:</strong> espandi/collassa</p>
                </div>
              </div>
            </div>
          </div>

          {/* ) : ( */}

          <Giornale userId={user?.id} />



          {/* {/* PROVA DISATTIVAZIONE)  */}
          

        </main> 




        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="fixed inset-0 z-50 md:hidden">
            <div className="fixed inset-0 bg-black bg-opacity-50" onClick={() => setIsMobileMenuOpen(false)} />
            <div className="fixed right-0 top-0 h-full w-64 bg-white shadow-lg">
              <div className="p-4">
                <button
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="p-2 rounded-lg hover:bg-gray-100 mb-4"
                >
                  <Menu className="w-5 h-5 text-gray-600" />
                </button>
                <div className="space-y-4">
                  <button className="w-full text-left px-4 py-2 hover:bg-gray-100 rounded-lg">
                    Impostazioni
                  </button>
                  <button className="w-full text-left px-4 py-2 hover:bg-gray-100 rounded-lg">
                    Esci
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Modal */}
        <ContoModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          conto={selectedConto}
          onSave={handleModalSubmit}
          mode={modalMode}
          contiList={conti}
        />
      </div>
    </DndProvider>
  );
}

export default App;
